import { useEffect } from "react";
import type { NewsItem } from "@/mocks/gossip";

const tagColor: Record<string, string> = {
  fresh: "bg-primary-100 text-primary-700",
  deny: "bg-secondary-100 text-secondary-700",
  respond: "bg-accent-100 text-accent-800",
  progress: "bg-background-200 text-foreground-700",
};

export default function ArticleModal({ item, onClose }: { item: NewsItem; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-foreground-950/70 px-4 py-8 backdrop-blur-sm md:py-12">
      <div className="fade-in relative w-full max-w-3xl overflow-hidden rounded-2xl bg-background-50">
        <div className="relative h-56 w-full overflow-hidden md:h-72">
          <img src={item.image} alt={item.imgAlt} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent"></div>
          <button
            onClick={onClose}
            aria-label="关闭"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950/60 text-background-50 transition-colors hover:bg-foreground-950/85"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6">
            <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold ${tagColor[item.cat]}`}>
              {item.catName} · {item.tag}
            </span>
          </div>
        </div>

        <div className="p-6 md:p-8">
          <h3 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">{item.title}</h3>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line"></i>
              扒哥吃瓜前线
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {item.time}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              阅读 {item.read}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              评论 {item.comment}
            </span>
          </div>

          <div className="mt-2 h-px w-full bg-background-200"></div>

          <div className="mt-6 flex flex-col gap-5">
            {item.content.map((p, i) => (
              <p key={i} className="text-[15px] leading-8 text-foreground-800">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-6 rounded-lg bg-background-100 p-4 text-xs leading-relaxed text-foreground-600">
            <span className="font-semibold text-foreground-800">内容说明：</span>
            本文基于公开可核验信源整理，事实与观点分开呈现，信息截至发布时点有效。事件如有新进展，我们将在对应条目下持续补充后续追踪，欢迎理性讨论。
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <a
              href="#subscribe"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-notification-3-line"></i>
              订阅，反转第一时间通知我
            </a>
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center gap-1 rounded-md px-4 py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-200/70"
            >
              返回列表
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}